export const GLOBAL_LANGUAGE = ['ko', 'en'] as const;
export type GlobalLanguage = (typeof GLOBAL_LANGUAGE)[number];

/**
 * 글로벌 에디터 UI 라벨 사전.
 * 키 컨벤션: '<ComponentName>.<area>.<field>'.
 * 번역 대상은 "에디터에서만 노출되는 라벨"로 한정한다(뷰어 출력 불변 — SDD §0 INVARIANT).
 * 동적 보간이 필요한 값은 '{{name}}' 토큰을 두고 사용처에서 t(key, { name }) 로 치환한다.
 */
export const messages: Record<GlobalLanguage, Record<string, string>> = {
  ko: {
    // 에디터 외곽 프레임
    'Editor.togglePreview': '미리보기',
    'Editor.header.file': '파일',
    'Editor.header.reset': '초기화',
    'Editor.header.load': '불러오기',
    'Editor.header.save': '저장',
    'Editor.layer.expand': '펼치기',
    'Editor.layer.collapse': '접기',
    'Editor.layer.duplicate': '복제',
    'Editor.layer.delete': '삭제',
    'Editor.inspector.tab.settings': '설정',
    'Editor.inspector.tab.logExposure': '로그/노출시간',
    'Editor.baseInspector.durationHeader': '노출시간 설정',
    'Editor.baseInspector.logLabel': '로그',
    'Editor.previewHeader.datePlaceholder': '날짜',
    'Editor.canvas.page': '페이지',
    'Editor.field.imageUrlPlaceholder': '이미지 URL 입력',
    'Editor.field.invalidImageUrl': '유효하지 않은 이미지 URL입니다.',
    'Editor.field.videoUrlPlaceholder': '비디오 URL 입력',
    'Editor.field.invalidVideoUrl': '유효하지 않은 비디오 URL입니다.',
    'Editor.field.videoFileHint': '10MB 이하의 비디오 파일만 업로드 가능합니다.',
    'Editor.duration.start': '시작',
    'Editor.duration.end': '종료',
    'Editor.duration.datePlaceholder': '날짜',

    // GlobalCountdown
    'GlobalCountdown.displayName': '카운트다운',
    'GlobalCountdown.inspector.color.black': '블랙',
    'GlobalCountdown.inspector.color.white': '화이트',
    'GlobalCountdown.inspector.subText.header': '카운트다운 설명문구',
    'GlobalCountdown.inspector.subText.maxHint': '최대 {{count}}자까지 입력 가능합니다.',
    'GlobalCountdown.inspector.align.header': '정렬',
    'GlobalCountdown.inspector.fontColor.header': '글자색',
    'GlobalCountdown.inspector.bgImage.header': '배경이미지',
    'GlobalCountdown.placeholder': '시간을 입력해주세요',

    // GlobalImage
    'GlobalImage.displayName': '이미지',
    'GlobalImage.inspector.image.header': '이미지',
    'GlobalImage.inspector.image.uploadLabel': '이미지 업로드',
    'GlobalImage.placeholder': '이미지를 등록해주세요.',

    // GlobalImageMapBraze (뷰어 노출 message/스낵바는 제외)
    'GlobalImageMapBraze.displayName': '이미지 영역 braze 알림 로깅',
    'GlobalImageMapBraze.inspector.eventName.header': 'Braze 커스텀 이벤트 이름',
    'GlobalImageMapBraze.inspector.eventName.hint': 'Braze로 적재할 이벤트명을 입력해주세요. 빈 칸일경우 이벤트가 적재되지 않습니다.',
    'GlobalImageMapBraze.inspector.toast.header': 'URL 미입력 시 나오는 토스트 메세지',
    'GlobalImageMapBraze.placeholder': '아이템을 추가해주세요.',

    // GlobalImageMapLink
    'GlobalImageMapLink.displayName': '이미지 영역 링크',

    // GlobalImageTabCarousel
    'GlobalImageTabCarousel.displayName': '이미지 탭 캐러셀',
    'GlobalImageTabCarousel.guide.minTabs': '탭을 추가해주세요. (최소 3개, 최대 10개 권장)',
    'GlobalImageTabCarousel.guide.checkItems': '이미지 탭 캐러셀 아이템을 확인해주세요. 이미지, 제목 미설정 탭은 필터링 됩니다. (최소 3개, 최대 10개 권장)',
    'GlobalImageTabCarouselItem.displayName': '이미지 탭 캐러셀 아이템',
    'GlobalImageTabCarouselItem.placeholder': '내용을 추가해주세요.',
    'GlobalImageTabCarouselItem.inspector.image.header': '이미지',
    'GlobalImageTabCarouselItem.inspector.image.uploadLabel': '이미지 업로드',
    'GlobalImageTabCarouselItem.inspector.title.header': '제목',
    'GlobalImageTabCarouselItem.inspector.title.placeholder': '탭 제목을 입력해주세요.',

    // GlobalProductList
    'GlobalProductList.displayName': '상품리스트',
    'GlobalProductList.placeholder': '상품을 추가해주세요.',
    'GlobalProductList.inspector.listType.header': '리스트 유형',
    'GlobalProductList.inspector.badgeType.header': '뱃지 유형',
    'GlobalProductList.inspector.buyButton.header': '구매하기 버튼 사용',
    'GlobalProductList.inspector.badge.colorOption': '뱃지 컬러',
    'GlobalProductList.itemAddModal.addButton': '상품 추가',
    'GlobalProductList.itemAddModal.title': '상품 추가',
    'GlobalProductList.itemAddModal.confirm': '확인',
    'GlobalProductList.itemAddModal.cancel': '취소',
    'GlobalProductList.itemAddModal.description': '추가하실 상품들의 id를 입력해주세요.',
    'GlobalProductList.itemAddModal.inputPlaceholder': '여러개를 조회할 경우 쉼표나 공백으로 구분해주세요.',
    'GlobalProductList.itemAddModal.numericError': '숫자와 쉼표만 입력할 수 있습니다.',
    'GlobalProductList.itemAddModal.cannotAdd': '{{ids}} 상품을 추가할 수 없습니다.',
    'GlobalProductList.itemAddModal.alreadyExist': '{{ids}} 상품이 이미 존재합니다.',
    'GlobalProductList.itemAddModal.duplicateRemoved': '중복으로 추가된 상품이 있어, 중복된 상품은 삭제하였습니다.',

    // GlobalProductListCarousel
    'GlobalProductListCarousel.displayName': '상품리스트 캐러셀',
    'GlobalProductListCarousel.placeholder': '상품을 추가해주세요.',

    // GlobalLink
    'GlobalLink.displayName': '링크',
    'GlobalLink.inspector.openType.header': '이동 방식',
    'GlobalLink.inspector.openType.currentWindow': '현재 창',
    'GlobalLink.inspector.openType.newWindow': '새 창',
    'GlobalLink.inspector.openType.anchor': '앵커',
    'GlobalLink.placeholder': '아이템을 추가해주세요.',

    // GlobalVideo
    'GlobalVideo.displayName': '비디오',
    'GlobalVideo.inspector.thumbnail.header': '썸네일',
    'GlobalVideo.inspector.video.header': '비디오',
    'GlobalVideo.inspector.loop.header': '반복재생',
    'GlobalVideo.inspector.mute.header': '음소거',
    'GlobalVideo.inspector.autoplay.header': '자동재생',
    'GlobalVideo.placeholder': '비디오를 등록해주세요.',

    // GlobalPromoCodeCopy
    'GlobalPromoCodeCopy.displayName': '코드 복사',
    'GlobalPromoCodeCopy.inspector.promoCode.header': '프로모션 코드',
    'GlobalPromoCodeCopy.inspector.promoCode.placeholder': '프로모션 코드를 입력해주세요.',
    'GlobalPromoCodeCopy.inspector.buttonBgColor.header': '버튼 배경 색상',
    'GlobalPromoCodeCopy.inspector.unitBgColor.header': '유닛 배경 색상',

    // 공유 resolver 유닛 (글로벌 에디터에도 노출)
    'Group.displayName': '그룹',
    'Group.placeholder': '아이템을 추가해주세요.',
    'Text.displayName': '텍스트',
    'Text.placeholder': '내용을 입력해주세요.',
    'Text.inspector.content.header': '내용',
    'Text.inspector.markdown': '마크다운 문법',
    'Text.inspector.size.header': '크기',
    'Text.inspector.weight.header': '굵기',
    'Text.inspector.align.header': '정렬',
    'Text.inspector.color.header': '글자색',
    'Tab.displayName': '탭',
    'Tab.placeholder': '탭을 추가해주세요.',
    'Tab.inspector.buttonColumn.header': '버튼 열',
    'TabContent.displayName': '탭 내용',
    'TabContent.placeholder': '아이템을 추가해주세요.',
    'TabItem.displayName': '탭 항목',
    'Accordion.displayName': '아코디언',
    'Accordion.button.expand': '아코디언 펼치기 버튼',
    'Accordion.button.collapse': '아코디언 접기 버튼',
    'Tab.button.on': '탭 버튼 on',
    'Tab.button.off': '탭 버튼 off',
    'Accordion.inspector.buttonPosition.header': '버튼 위치',
    'Accordion.inspector.initialExpanded.header': '최초 펼침 상태',
    'Accordion.inspector.peek.header': '살짝 보여주기',
    'AccordionContent.displayName': '아코디언 내용',
    'AccordionContent.placeholder': '아이템을 추가해주세요.',
    'Carousel.displayName': '캐로셀',
    'Carousel.placeholder': '아이템을 추가해주세요.',
    'Carousel.inspector.align.center': '중앙',
    'Carousel.inspector.align.left': '왼쪽',
    'Carousel.inspector.autoplay.header': '자동재생',
    'Carousel.inspector.autoplayInterval.header': '자동재생 인터벌',
    'Carousel.inspector.loop.header': '루프',
    'Carousel.inspector.itemWidth.header': '아이템 너비',
    'Carousel.inspector.itemAlign.header': '아이템 정렬',
    'Carousel.inspector.pagination.header': '페이지네이션',
    'Carousel.inspector.arrow.header': '화살표',
    'AnchorTarget.displayName': '앵커 타겟',
    'AnchorTarget.inspector.id.header': '앵커 타겟 id',
    'AnchorTarget.placeholder.scroll': '{{targetId}}로 앵커 URL 설정시 여기로 스크롤됩니다',
    'AnchorTarget.placeholder.enterId': '앵커 타켓 id를 입력해주세요',
    'StickyTab.displayName': '스티키 탭',
    'StickyTab.placeholder': '탭을 추가해주세요.',
    'StickyTabItem.displayName': '스티키 탭 항목',
    'StickyTabItem.inspector.id.header': '탭 아이템 id',
    'StickyTabItem.inspector.id.placeholder': '앵커링 타켓으로 지정할 id를 입력해주세요.',
    'StickyTabItem.placeholder': '탭 아이템 id를 입력해주세요.',
    'TextTabItem.displayName': '텍스트 탭 항목',
    'TextTabItem.placeholder': '탭 타이틀을 입력해주세요.',
    'TextTabItem.inspector.title.header': '탭 타이틀',
    'TextTabItem.inspector.subTitle.header': '탭 서브 타이틀',
  },
  en: {
    // Editor frame
    'Editor.togglePreview': 'Preview',
    'Editor.header.file': 'File',
    'Editor.header.reset': 'Reset',
    'Editor.header.load': 'Load',
    'Editor.header.save': 'Save',
    'Editor.layer.expand': 'Expand',
    'Editor.layer.collapse': 'Collapse',
    'Editor.layer.duplicate': 'Duplicate',
    'Editor.layer.delete': 'Delete',
    'Editor.inspector.tab.settings': 'Settings',
    'Editor.inspector.tab.logExposure': 'Log / Display Time',
    'Editor.baseInspector.durationHeader': 'Display Schedule',
    'Editor.baseInspector.logLabel': 'Log',
    'Editor.previewHeader.datePlaceholder': 'Date',
    'Editor.canvas.page': 'Page',
    'Editor.field.imageUrlPlaceholder': 'Enter image URL',
    'Editor.field.invalidImageUrl': 'Invalid image URL.',
    'Editor.field.videoUrlPlaceholder': 'Enter video URL',
    'Editor.field.invalidVideoUrl': 'Invalid video URL.',
    'Editor.field.videoFileHint': 'Only video files up to 10MB can be uploaded.',
    'Editor.duration.start': 'Start',
    'Editor.duration.end': 'End',
    'Editor.duration.datePlaceholder': 'Date',

    // GlobalCountdown
    'GlobalCountdown.displayName': 'Countdown',
    'GlobalCountdown.inspector.color.black': 'Black',
    'GlobalCountdown.inspector.color.white': 'White',
    'GlobalCountdown.inspector.subText.header': 'Countdown Description',
    'GlobalCountdown.inspector.subText.maxHint': 'Up to {{count}} characters allowed.',
    'GlobalCountdown.inspector.align.header': 'Alignment',
    'GlobalCountdown.inspector.fontColor.header': 'Font Color',
    'GlobalCountdown.inspector.bgImage.header': 'Background Image',
    'GlobalCountdown.placeholder': 'Enter a time',

    // GlobalImage
    'GlobalImage.displayName': 'Image',
    'GlobalImage.inspector.image.header': 'Image',
    'GlobalImage.inspector.image.uploadLabel': 'Upload Image',
    'GlobalImage.placeholder': 'Please add an image.',

    // GlobalImageMapBraze
    'GlobalImageMapBraze.displayName': 'Image Area Braze Notification Logging',
    'GlobalImageMapBraze.inspector.eventName.header': 'Braze Custom Event Name',
    'GlobalImageMapBraze.inspector.eventName.hint': 'Enter the event name to send to Braze. If left blank, no event is sent.',
    'GlobalImageMapBraze.inspector.toast.header': 'Toast message shown when URL is empty',
    'GlobalImageMapBraze.placeholder': 'Please add an item.',

    // GlobalImageMapLink
    'GlobalImageMapLink.displayName': 'Image Area Link',

    // GlobalImageTabCarousel
    'GlobalImageTabCarousel.displayName': 'Image Tab Carousel',
    'GlobalImageTabCarousel.guide.minTabs': 'Please add tabs. (Min 3, max 10 recommended)',
    'GlobalImageTabCarousel.guide.checkItems': 'Check the carousel items. Tabs without an image or title are filtered out. (Min 3, max 10 recommended)',
    'GlobalImageTabCarouselItem.displayName': 'Image Tab Carousel Item',
    'GlobalImageTabCarouselItem.placeholder': 'Please add content.',
    'GlobalImageTabCarouselItem.inspector.image.header': 'Image',
    'GlobalImageTabCarouselItem.inspector.image.uploadLabel': 'Upload Image',
    'GlobalImageTabCarouselItem.inspector.title.header': 'Title',
    'GlobalImageTabCarouselItem.inspector.title.placeholder': 'Enter the tab title.',

    // GlobalProductList
    'GlobalProductList.displayName': 'Product List',
    'GlobalProductList.placeholder': 'Please add products.',
    'GlobalProductList.inspector.listType.header': 'List Type',
    'GlobalProductList.inspector.badgeType.header': 'Badge Type',
    'GlobalProductList.inspector.buyButton.header': 'Use Buy Button',
    'GlobalProductList.inspector.badge.colorOption': 'Badge Color',
    'GlobalProductList.itemAddModal.addButton': 'Add Product',
    'GlobalProductList.itemAddModal.title': 'Add Product',
    'GlobalProductList.itemAddModal.confirm': 'Confirm',
    'GlobalProductList.itemAddModal.cancel': 'Cancel',
    'GlobalProductList.itemAddModal.description': 'Enter the IDs of the products to add.',
    'GlobalProductList.itemAddModal.inputPlaceholder': 'For multiple, separate with commas or spaces.',
    'GlobalProductList.itemAddModal.numericError': 'Only numbers and commas are allowed.',
    'GlobalProductList.itemAddModal.cannotAdd': 'Cannot add products: {{ids}}.',
    'GlobalProductList.itemAddModal.alreadyExist': 'Products already exist: {{ids}}.',
    'GlobalProductList.itemAddModal.duplicateRemoved': 'Duplicate products were found and removed.',

    // GlobalProductListCarousel
    'GlobalProductListCarousel.displayName': 'Product List Carousel',
    'GlobalProductListCarousel.placeholder': 'Please add products.',

    // GlobalLink
    'GlobalLink.displayName': 'Link',
    'GlobalLink.inspector.openType.header': 'Open Method',
    'GlobalLink.inspector.openType.currentWindow': 'Current Window',
    'GlobalLink.inspector.openType.newWindow': 'New Window',
    'GlobalLink.inspector.openType.anchor': 'Anchor',
    'GlobalLink.placeholder': 'Please add an item.',

    // GlobalVideo
    'GlobalVideo.displayName': 'Video',
    'GlobalVideo.inspector.thumbnail.header': 'Thumbnail',
    'GlobalVideo.inspector.video.header': 'Video',
    'GlobalVideo.inspector.loop.header': 'Loop',
    'GlobalVideo.inspector.mute.header': 'Mute',
    'GlobalVideo.inspector.autoplay.header': 'Autoplay',
    'GlobalVideo.placeholder': 'Please add a video.',

    // GlobalPromoCodeCopy
    'GlobalPromoCodeCopy.displayName': 'Copy Code',
    'GlobalPromoCodeCopy.inspector.promoCode.header': 'Promo Code',
    'GlobalPromoCodeCopy.inspector.promoCode.placeholder': 'Enter the promo code.',
    'GlobalPromoCodeCopy.inspector.buttonBgColor.header': 'Button Background Color',
    'GlobalPromoCodeCopy.inspector.unitBgColor.header': 'Unit Background Color',

    // Shared resolver units (also shown in the global editor)
    'Group.displayName': 'Group',
    'Group.placeholder': 'Please add an item.',
    'Text.displayName': 'Text',
    'Text.placeholder': 'Enter content.',
    'Text.inspector.content.header': 'Content',
    'Text.inspector.markdown': 'Markdown syntax',
    'Text.inspector.size.header': 'Size',
    'Text.inspector.weight.header': 'Weight',
    'Text.inspector.align.header': 'Alignment',
    'Text.inspector.color.header': 'Font Color',
    'Tab.displayName': 'Tab',
    'Tab.placeholder': 'Please add tabs.',
    'Tab.inspector.buttonColumn.header': 'Button Columns',
    'TabContent.displayName': 'Tab Content',
    'TabContent.placeholder': 'Please add an item.',
    'TabItem.displayName': 'Tab Item',
    'Accordion.displayName': 'Accordion',
    'Accordion.button.expand': 'Accordion Expand Button',
    'Accordion.button.collapse': 'Accordion Collapse Button',
    'Tab.button.on': 'Tab Button (On)',
    'Tab.button.off': 'Tab Button (Off)',
    'Accordion.inspector.buttonPosition.header': 'Button Position',
    'Accordion.inspector.initialExpanded.header': 'Initially Expanded',
    'Accordion.inspector.peek.header': 'Peek',
    'AccordionContent.displayName': 'Accordion Content',
    'AccordionContent.placeholder': 'Please add an item.',
    'Carousel.displayName': 'Carousel',
    'Carousel.placeholder': 'Please add an item.',
    'Carousel.inspector.align.center': 'Center',
    'Carousel.inspector.align.left': 'Left',
    'Carousel.inspector.autoplay.header': 'Autoplay',
    'Carousel.inspector.autoplayInterval.header': 'Autoplay Interval',
    'Carousel.inspector.loop.header': 'Loop',
    'Carousel.inspector.itemWidth.header': 'Item Width',
    'Carousel.inspector.itemAlign.header': 'Item Alignment',
    'Carousel.inspector.pagination.header': 'Pagination',
    'Carousel.inspector.arrow.header': 'Arrows',
    'AnchorTarget.displayName': 'Anchor Target',
    'AnchorTarget.inspector.id.header': 'Anchor Target ID',
    'AnchorTarget.placeholder.scroll': 'Scrolls here when an anchor URL is set to {{targetId}}',
    'AnchorTarget.placeholder.enterId': 'Enter the anchor target ID',
    'StickyTab.displayName': 'Sticky Tab',
    'StickyTab.placeholder': 'Please add tabs.',
    'StickyTabItem.displayName': 'Sticky Tab Item',
    'StickyTabItem.inspector.id.header': 'Tab Item ID',
    'StickyTabItem.inspector.id.placeholder': 'Enter the ID to use as the anchoring target.',
    'StickyTabItem.placeholder': 'Enter the tab item ID.',
    'TextTabItem.displayName': 'Text Tab Item',
    'TextTabItem.placeholder': 'Enter the tab title.',
    'TextTabItem.inspector.title.header': 'Tab Title',
    'TextTabItem.inspector.subTitle.header': 'Tab Subtitle',
  },
};
